import Vue from "vue"
import { store } from "@/store"
import router from "@/router/router"
import Person from "@/types/Person.interface"
import Media from "@/types/Media.interface"
import Button from "@/types/components/Button.interface"
import Tenant from "@/types/Tenant.interface"

export default {
    /**
     * Adds an item to an array using the standardized 'id'
     * property
     * 
     * @param {item} item - item we're adding 
     * @param {array} array - the array we're adding the item to
     */
    addItemById(item: any, array: Array<any>, unshift = false) {
        //  Check if the item is already in the list
        const i = array.findIndex((x) => x.id === item.id)

        //  If the item does NOT exist, add it
        if (i === -1) {
            //  If 'unshift' is set, add the item to the begining of the array
            if (unshift) array.unshift(item)
            //  Else add item to the end of the array
            else array.push(item)
        }
        //  Else, update the item
        else Vue.set(array, i, item)

        return array
    },
    /**
     * Removes an item from an array using the standardized 'id'
     * property
     * 
     * @param {any} item - item we're removing 
     * @param {array} array - the array we're removing the item from
     */
    removeItemById(item: any, array: Array<any>) {
        //  Check if the item is already in the list
        const i = array.findIndex((x) => x.id === item.id)

        //  If the item exists, remove it
        if (i > -1) array.splice(i, 1)

        //  Return the altered array
        return array
    },
    /**
     * Merge two arrays using the standardized 'id'
     * property
     * 
     * @param {array} newItems - items we're adding/updating the array with 
     * @param {array} existingItems - the existing items that are being updated
     */
    updateArrayById(newItems: Array<any>, existingItems: Array<any>) {
        for (const x of newItems) {
            //  Add/Update the item in the existing Items
            this.addItemById(x, existingItems)
        }
        //  return the updated array
        return existingItems
    }
}

// Use Lodash to clone a string, number, array, or object
export function cloneDeep(input: any): any {
    //  Get the cloneDeep function from Lodash library
    const clone = require('lodash/cloneDeep')

    //  Clone the input
    const output = clone(input)

    //  Return the clone
    return output
}

export async function messageFeedCSVExport(defaultParams: boolean): Promise<void> {
      //  Send export request to the API
      const response = await store.dispatch('messagesTableCsvExport', defaultParams)
    
      //  If the request failed, do nothing
      if (!response) return

      //  Else, notify user of export
      store.dispatch('dialogOpen', {
          type: 'MESSAGE',
          title: 'Export in Progress',
          icon: 'mdi-database-export',
          text: response.message
      })
}   

export function goBack(item: Button): void {
    //  Check if a 'sender' is set in the url
    const sender = window.root.$route.query.sender

    //  If a sender exists, route to send
    if (sender) router.push(sender)

    //  If we are on mobile app, we have to push, not do back or else we infinite loop
    //  If the route doesn't include a route to back to, we default to home
    else if (store.getters.isApp) item ? router.push(item.back) : router.push('/home')

    //  Else, go back
    else router.back()
}

export function isValidUrl(str: string): boolean {
    // If the string is a valid URL, a URL object is created, and true is returned
    // If the String is not a valid URL, a Tyeperror exception is thrown, and false is returned

    try {
        return Boolean(new URL(str))
    } catch (e) {
        return false
    }
}

//  Use Lodash to return the difference between two objects (used for sparse updates)
export function differenceWith(objA: any, objB: any): any {
    //  Create a response object
    const response = {}

    //  Get the lodash functions
    const differenceWith = require('lodash/differenceWith')
    const isEqual = require('lodash/isEqual')
    const toPairs = require('lodash/toPairs')

    //  Get the difference between the objects
    const diff = differenceWith(toPairs(objA), toPairs(objB), isEqual)

    //  Convert array of pairs, to object
    diff.forEach((x) => response[x[0]] = x[1])

    return response
}

//  Use Lodash to check if an value is empty
export function isEmpty(input: any): boolean {
    //  Get the lodash function
    const isEmpty = require('lodash/isEmpty')

    //  Get the response
    const output = isEmpty(input)

    //  Return the result
    return output
}

//  Use Lodash to deep compare two objects
export function isEqual(objA: any, objB: any): boolean {
    //  Get the lodash 'isEqual' library
    const isEqual = require('lodash/isEqual')

    //  Check if the two parameters are equal
    const equal = isEqual(objA, objB)

    //  Return the result
    return equal
}

//  Use dot notated path to get a value from a nested object
export function objectGet(path: string, obj: object): any {
    if (!path || !obj) return null

    //  Get the nested attributes from the path
    var properties = path.split('.')

    //  Get the value
    const value = properties.reduce((prev, curr) => prev && prev[curr], obj)

    //  If the value is undefined, return null
    if (!value) return null

    //  Else, return the value
    return value;
}

//  Use dot notated path to set a value in a nested object
export function objectSet(path: string | string[], value: any, obj: object): boolean {
    // this is a super simple parsing, you will want to make this more complex to handle correctly any path
    // it will split by the dots at first and then simply pass along the array (on next iterations)
    let properties = Array.isArray(path) ? path : path.split('.')

    // Not yet at the last property so keep digging
    if (properties.length > 1) {
        // The property doesn't exists OR is not an object (and so we overwritte it) so we create it
        if (!obj.hasOwnProperty(properties[0]) || typeof obj[properties[0]] !== 'object')
            obj[properties[0]] = {}
        // We iterate.
        return objectSet(properties.slice(1), value, obj[properties[0]])
        // This is the last property - the one where to set the value
    } else {
        // We set the value to the last property
        obj[properties[0]] = value
        return true // this is the end
    }
}

//  Pops a dialog with a person search bar and the ability to create a new contact
export function popNewMessage(callback: Function = null): void {
    //  Pop a new message selector
    store.dispatch('dialogOpen', {
        type: 'APP',
        title: 'New Message',
        icon: 'mdi-message-processing',
        color: 'primary',
        component: 'new-message-selector',
        closeOnSubmit: true,
        class: 'pa-4',
        callback: (person: Person): void => {
            //  We should close everything just in case
            store.dispatch('dialogClose', 'APP')

            //  If we have a callback, use it
            if (callback) return callback(person)

            //  Else, do default behavior of routing
            router.push(`/inbox?channel=${person.primary_channel_id}`)
        }
    })
}

//  Opens the media preview
export function popPreview(media: Media): void {
    //  Open our media preview
    store.dispatch('dialogOpen', {
        type: 'APP',
        component: 'media-preview',
        closeable: media.type.includes('image') || store.getters.isMobile(),
        closeOnSubmit: true,
        fullscreen: true,
        backgroundColor: 'rgba(0,0,0,0.5) !important',
        btnColor: 'white',
        props: {
            media: media
        }
    })
}

//  Generates a random alphanumeric 36 character key
export function randomKey(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

//  Pop a signout confirm dialog
export function signoutConfirm(): void {
    store.dispatch('dialogOpen', {
        type: 'CONFIRM',
        title: 'Sign out?',
        text: 'Are you sure you want to sign out?',
        icon: 'mdi-account-lock',
        color: 'primary',
        callback: (result: boolean): void => {
            //  If they answered no, do nothing
            if (!result) return

            //  Invalidate the auth token
            store.dispatch('logout')

            //  Reset the authentication token, since it is no good any longer
            store.commit('authTokenSet', null)

            //  Reset the two factor auth token, since it is no good any longer
            store.commit('authTwoFactorTokenSet', null)

            //  Reset the authenticated user (this also publishes onUserLogout event)
            store.commit('authUserSet', null)

            //  Navigate to signin
            router.push('/')
        }
    })
}

//  Pop an app dialog with a timezone selector
export function timezoneUpdate(): void {
    store.dispatch('dialogOpen', {
        type: 'APP',
        title: 'Set Timezone',
        icon: 'mdi-map-clock-outline',
        color: 'primary',
        component: 'time-zone-form',
        class: 'pa-4',
        callback: (tenant: Tenant): void => {
            //  If the tenant does NOT exist, do nothing
            if (!tenant) return

            //  Close the app dialog
            store.commit('dialogCloseAll', 'APP')

            //  Else, notify user of success
            store.commit('appNotificationBarSet', {
                message: 'Timezone Successfully Updated',
                type: 'success'
            })
        },
        props: { autoUpdate: true }
    })
}

//  The number of days left in our trial, formatted
export function trialDays(): string {
    //  Grab our subscription
    const sub = store.getters.subscription

    //  Grab the period end for the sub
    const periodEnd = sub.current_period_end

    //  Get the date that our trial ends at
    const trialEndDate = new Date(periodEnd * 1000)

    //  Get the current date
    const now = new Date()

    //  Grab the difference between the two dates
    const diff = trialEndDate.getTime() - now.getTime()

    //  Convert this difference into days (we always want it rounded)
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24))

    //  If we have more than 1 day, make it plural
    return diffDays > 1
        ? `${diffDays} Days`
        : `${diffDays} Day`
}

//  Unmask a value
export function unmask(maskedValue: string, mask: string): string {
    const defaultTokens = ['#', 'X', 'S', 'A', 'a', '!'];
    let unmaskedValue = '';
    let isToken;

    for (let i = 0; i < maskedValue.length; i++) {
        isToken = false;
        for (let j = 0; j < defaultTokens.length; j++) {
            if (mask[i] == defaultTokens[j]) {
                isToken = true;
            }
        }

        if (isToken) {
            unmaskedValue += maskedValue[i];
        }
    }

    return unmaskedValue;
}

//  Check if the tenant has a timezone set, if not pop a dialog
export function validateTimezone(): void {
    //  Check if the current tenant has a timezone set
    const timezone = store.getters.tenant.default_timezone

    //  If a timezone exists, do nothing
    if (timezone) return

    //  Else, pop the timezone dialog
    store.dispatch('dialogOpen', {
        type: 'APP',
        title: 'Select a time zone',
        icon: 'mdi-map-clock-outline',
        color: 'primary',
        component: 'time-zone-form',
        closeable: false,
        props: {
            autoClose: true,
            autoUpdate: true
        }
    })
}